import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { useDeviceSize } from '@vmos2/providers';

import { useComponentTheme } from '../useComponentTheme';
import { Image } from '../image/Image';

type Props = {
  uuid?: string,
  title: string,
  placeholder?: string,
  hideTitle?: boolean,
  scope?: string,
  mode?: 'CategoryPoster',
  className?: string,
  as?: string | React.Node,
  to?: string,
  params?: { [index: string]: mixed },
  imageSrc?: String,
  externalImageSrc?: String,
  imageView?: String,
};

export const PosterCard = ({
  uuid,
  title,
  placeholder,
  hideTitle,
  scope,
  mode,
  className,
  as,
  to,
  params,
  imageSrc,
  externalImageSrc,
  imageView,
}: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.Poster, mode);
  const [imageLoadError, setImageLoadError] = useState(false);
  const { isMobile } = useDeviceSize();

  const imageViewWidth = useMemo(() => {
    if (imageView === 'double' && isMobile) return '100vw';
    if (imageView === 'triple' || imageView === 'double' || isMobile) return '50vw';
    return '33vw';
  }, [isMobile, imageView]);

  return (
    <LiWrapper imageLoadError={imageLoadError} componentTheme={componentTheme} className={className}>
      <Container
        ariaLabel={title}
        as={as}
        to={to}
        params={params}
        componentTheme={componentTheme}
        className={className}
      >
        <ImageContainer
          uuid={uuid}
          alt={title}
          aria-hidden="true"
          placeholder={placeholder}
          onError={setImageLoadError}
          scope={scope}
          src={imageSrc}
          externalSrc={externalImageSrc}
          sizes={imageViewWidth}
        />
        {!hideTitle && (
          <TitleWrapper componentTheme={componentTheme}>
            <Title aria-hidden="true" imageLoadError={imageLoadError} componentTheme={componentTheme}>
              {title}
            </Title>
          </TitleWrapper>
        )}
      </Container>
    </LiWrapper>
  );
};

const LiWrapper = styled('li')`
  list-style: none;
  margin: 0;
  padding: 0;

  position: relative;
  border-radius: ${({ componentTheme }) => componentTheme.Container.borderRadius};
  box-shadow: ${({ componentTheme }) => componentTheme.Container.boxShadow};
  overflow: hidden;

  ${({ theme, imageLoadError }) =>
    imageLoadError &&
    `background: ${theme.color.lighterGrey};
  `}
`;

const Container = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const ImageContainer = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: -1;
  object-fit: cover;
`;

const TitleWrapper = styled('div')`
  position: absolute;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: ${({ componentTheme }) => componentTheme.Container.alignItems};
  justify-content: ${({ componentTheme }) => componentTheme.Container.textAlign};
  top: 0;
  padding: 16px;
`;

const Title = styled('h4')`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.menu.tileFontWeight};
  color: ${({ componentTheme }) => componentTheme.Title.color};
  font-family: ${({ theme, componentTheme }) =>
    componentTheme.Title.secondaryFontFamily ? theme.typography.secondaryFontFamily : 'inherit'};
  text-transform: ${({ componentTheme }) => componentTheme.Title.textTransform};

  ${({ theme, imageLoadError }) =>
    imageLoadError &&
    `color: ${theme.color.black};
  `}

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    background-color: ${({ componentTheme }) => componentTheme.Title.backgroundColor};
    border-radius: ${({ componentTheme }) => componentTheme.Title.borderRadius};
    padding: ${({ componentTheme }) => componentTheme.Title.padding};
  }

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    font-size: ${({ componentTheme }) => componentTheme.Title.fontSize};
  }
`;
